import { useEffect, useState } from "react";
import { MdOutlineCurrencyRupee, MdOutlineWifiTethering } from "react-icons/md";
import ModalElement from "../../../Common/ModalElement"
import InputElement from "../../../Common/InputElement";
import ButtonElement from "../../../Common/ButtonElement";
import SearchableDropdown from "../../../Common/SearchDropdown";
import { validateFields, commonOnChange, commonOnSelect, isValidObject, parseNumber } from "../../../utils";
import { LuBuilding2 } from "react-icons/lu";
import { RiTruckLine } from "react-icons/ri";
import { appApis, dropdownDefaultList } from "../../../constants";
import { useApiRequest } from "../../../hooks/useApiRequest";
import { shallowEqual, useSelector } from "react-redux";


const Billing = ({ show, hideModal, defaultData, getInvoiceData }) => {
    const [details, setDetails] = useState({
        companyName: "",
        vehicleNo: "",
        gpsImeiNo: "",
        billingType: "",
        billingCycle: "Yearly",
        lastBillingDate: null,
        billingAmount: "",
        renewalAmount: "",
        billingCompany: "",
        gstApplied: "Yes",
        gstPercentage: "",
        billingTypeErr: null,
        billingCycleErr: null,
        billingAmountErr: null,
        renewalAmountErr: null,
        gstPercentageErr: null
    });
    const { filters } = useSelector(({ inventory }) => ({
        filters: inventory.filters
    }), shallowEqual);
    const isEdit = defaultData?.billing_company_name ? true : false;

    const { companyName, vehicleNo, gpsImeiNo, billingType, billingCycle, lastBillingDate, billingAmount, renewalAmount, billingCompany, gstApplied, gstPercentage, billingAmountErr, gstPercentageErr, billingTypeErr, billingCycleErr, billingCompanyErr, renewalAmountErr } = details;

    const onChangeHandler = (event) => commonOnChange(event, setDetails);
    const onSelectHandler = (event) => commonOnSelect(event, setDetails);
    const { request: submitRequest, loading } = useApiRequest();

    const submit = () => {
        const obj = gstApplied == "Yes" ? { gstPercentageErr: [gstPercentage, "text"] } : {}
        validateFields(
            {
                billingTypeErr: [billingType, "text"],
                billingCycleErr: [billingCycle, "text"],
                billingAmountErr: [billingAmount, "number"],
                renewalAmountErr: [renewalAmount, "number"],
                billingCompanyErr: [billingCompany, "text"],
                ...obj
            },
            setDetails,
            () => {
                const payload = {
                    billingType,
                    billingCycle,
                    billingAmount: parseNumber(billingAmount),
                    renewalAmount: parseNumber(renewalAmount),
                    billingCompanyId: details?.billingCompanyId,
                    gstApplicable: gstApplied == "Yes",
                    gstPercentage: gstApplied == "Yes" ? details?.gstPercentageId || null : null
                }
                if(isEdit) payload["lastBillingDate"] = lastBillingDate;
                submitRequest({
                    method: isEdit ? "PUT" : "POST",
                    url: `${isEdit ? appApis.updateBilling : appApis.addBilling}?billingId=${defaultData?.current_billing_id}`,
                    body: payload,
                    onSuccess: () => {
                        setTimeout(() => {
                            hideModal();
                            getInvoiceData();
                        }, 1000);
                    }
                })
            }
        )
    }

    useEffect(() => {
       if(isValidObject(defaultData)){
        setDetails(prev => ({...prev,
            companyName: defaultData?.companyName,
            vehicleNo: defaultData?.vehicleRegno,
            gpsImeiNo: defaultData?.gpsImei,
            billingType: defaultData?.billing_type,
            lastBillingDate: defaultData?.last_billing_date ?? null,
            billingAmount: isEdit ? defaultData?.billing_amount : "",
            renewalAmount: isEdit ? defaultData?.renewal_amount : "",
            billingCompany: isEdit ? defaultData?.billing_company_name ?? "" : "",
            billingCompanyId: isEdit ? defaultData?.billingCompnayId ?? null : "",
            gstPercentage: isEdit ? defaultData?.gst_percentage ? `${defaultData?.gst_percentage}%` : "" : "",
            gstPercentageId: isEdit ? defaultData?.gst_percentage ?? "" : "",
            gstApplied: defaultData?.gst_applicable ? "Yes" : "No",
        }))
       }
    }, [defaultData])

    return <ModalElement size="lg" show={show} title={`${isEdit ? "Edit" : "Add"} Billing`} onHide={hideModal}>
        <div className="add-company text-start">
            <div className="company-details mb-3">
                <div className="row">
                    <div className="col-lg-4 col-md-6 mb-2">
                        <InputElement
                            disable={true}
                            label={"Company Name"}
                            placeholder={"Enter Company Name"}
                            name={"companyName"}
                            value={companyName}
                            icon={<LuBuilding2 size={20} />}
                            onChange={onChangeHandler}
                        />
                    </div>
                    <div className="col-lg-4 col-md-6 mb-2">
                        <InputElement
                            disable={true}
                            label={"Vehicle Number"}
                            placeholder={"Enter Vehicle Number"}
                            name={"vehicleNo"}
                            value={vehicleNo}
                            icon={<RiTruckLine size={20} />}
                            onChange={onChangeHandler}
                        />
                    </div>
                    <div className="col-lg-4 col-md-6 mb-2">
                        <InputElement
                            disable={true}
                            label={"GPS IMEI Number"}
                            placeholder={"Enter GPS IMEI Number"}
                            name={"gpsImeiNo"}
                            value={gpsImeiNo}
                            icon={<MdOutlineWifiTethering size={20} />}
                            onChange={onChangeHandler}
                        />
                    </div>
                </div>
            </div>
            <div className="company-details mb-3">
                <h5 className="mb-3">Billing Details</h5>
                <div className="row">
                    <div className="col-md-6 mb-2">
                        <SearchableDropdown
                            disabled={isEdit}
                            label={"Billing Type"}
                            placeholder={"Select Billing Type"}
                            value={billingType}
                            error={billingTypeErr}
                            options={dropdownDefaultList.billingType}
                            onSelect={(value) => onSelectHandler({ target: { name: "billingType", value } })}
                        />
                    </div>
                    <div className="col-md-6 mb-2">
                        <SearchableDropdown
                            disabled={isEdit}
                            label={"Billing Cycle"}
                            placeholder={"Select Billing Cycle"}
                            value={billingCycle}
                            error={billingCycleErr}
                            options={dropdownDefaultList.billingCycle}
                            onSelect={(value) => onSelectHandler({ target: { name: "billingCycle", value } })}
                        />
                    </div>
                    <div className={`col-md-${isEdit ? 4 : 6} mb-2`}>
                        <InputElement
                            label={"Billing Amount"}
                            placeholder={"Enter Billing Amount"}
                            name={"billingAmount"}
                            value={billingAmount}
                            error={billingAmountErr}
                            icon={<MdOutlineCurrencyRupee size={20} />}
                            onChange={onChangeHandler}
                        />
                    </div>
                    <div className={`col-md-${isEdit ? 4 : 6} mb-2`}>
                        <InputElement
                            label={"Renewal Amount"}
                            placeholder={"Enter Renewal Amount"}
                            name={"renewalAmount"}
                            value={renewalAmount}
                            error={renewalAmountErr}
                            icon={<MdOutlineCurrencyRupee size={20} />}
                            onChange={onChangeHandler}
                        />
                    </div>
                    {isEdit ? <div className={`col-lg-4 mb-2`}>
                        <InputElement
                            type="date"
                            label={"Last Billing Date"}
                            placeholder={"Enter Last Billing Date"}
                            name={"lastBillingDate"}
                            value={lastBillingDate}
                            error={false}
                            onChange={onChangeHandler}
                        />
                    </div> : null}
                    <div className="col-lg-4 col-md-6 mb-2">
                        <SearchableDropdown
                            label={"Billing Company"}
                            placeholder={"Select Billing Company"}
                            value={billingCompany}
                            error={billingCompanyErr}
                            options={filters?.billingCompanies ?? []}
                            onSelect={(value) => onSelectHandler({ target: { name: "billingCompany", value } })}
                        />
                    </div>
                    <div className="col-lg-4 col-md-6 mb-2">
                        <SearchableDropdown
                            label={"GST Applicable"}
                            placeholder={"Select GST Applicable"}
                            value={gstApplied}
                            error={false}
                            options={dropdownDefaultList.confirmation}
                            onSelect={(value) => onSelectHandler({ target: { name: "gstApplied", value } })}
                        />
                    </div>
                    {gstApplied == "Yes" ? <div className="col-lg-4 col-md-6 mb-2">
                        <SearchableDropdown
                            label={"GST Percentage"}
                            placeholder={"Select GST Percentage"}
                            value={gstPercentage}
                            error={gstPercentageErr}
                            options={dropdownDefaultList.gstPercentage}
                            onSelect={(value) => onSelectHandler({ target: { name: "gstPercentage", value } })}
                        />
                    </div> : null}
                </div>
            </div>
            <div className="submit-btn text-center my-4">
                <ButtonElement text={"Submit"} loading={loading} cssClass="small-radius mb-1" onClick={submit} variant="btn-outline-primary" />
            </div>
        </div>
    </ModalElement>
}

export default Billing;